<template>
  <div class="search_view">
    <div class="layout">
      <!-- Sidebar -->
      <aside class="sidebar">
        <div class="sidebar_header">
          <span class="sidebar_title">● FILTRY</span>
          <span class="active_badge" v-if="activeFilterCount > 0">{{ activeFilterCount }}</span>
        </div>

        <div class="filter_group">
          <label class="filter_label">Marka</label>
          <input
            class="filter_input filter_search"
            type="text"
            v-model="makerSearch"
            placeholder="Szukaj marki..."
          />
          <div class="option_list">
            <button
              class="option_item"
              :class="{ active: filters.maker === '' }"
              @click="selectMaker('')"
            >Wszystkie</button>
            <button
              v-for="m in filteredMakes"
              :key="m"
              class="option_item"
              :class="{ active: filters.maker === m }"
              @click="selectMaker(m)"
            >{{ m }}</button>
            <div class="option_empty" v-if="filteredMakes.length === 0">Brak wyników</div>
          </div>
        </div>

        <div class="filter_group">
          <label class="filter_label">Model</label>
          <div class="option_hint" v-if="!filters.maker">Najpierw wybierz markę</div>
          <template v-else>
            <input
              class="filter_input filter_search"
              type="text"
              v-model="modelSearch"
              placeholder="Szukaj modelu..."
            />
            <div class="option_list">
              <button
                class="option_item"
                :class="{ active: filters.model === '' }"
                @click="selectModel('')"
              >Wszystkie</button>
              <button
                v-for="m in filteredModels"
                :key="m"
                class="option_item"
                :class="{ active: filters.model === m }"
                @click="selectModel(m)"
              >{{ m }}</button>
              <div class="option_empty" v-if="filteredModels.length === 0">Brak wyników</div>
            </div>
          </template>
        </div>

        <div class="filter_group">
          <label class="filter_label">Rok produkcji</label>
          <div class="range_row">
            <input
              class="filter_input"
              type="number"
              v-model.number="filters.year_from"
              placeholder="Od"
              @change="applyFilters"
              min="1970"
              max="2030"
            />
            <span class="range_sep">–</span>
            <input
              class="filter_input"
              type="number"
              v-model.number="filters.year_to"
              placeholder="Do"
              @change="applyFilters"
              min="1970"
              max="2030"
            />
          </div>
        </div>

        <div class="filter_group">
          <label class="filter_label">Cena wywoławcza (JPY)</label>
          <div class="range_row">
            <input
              class="filter_input"
              type="number"
              v-model.number="filters.price_from"
              placeholder="Od"
              @change="applyFilters"
              min="0"
            />
            <span class="range_sep">–</span>
            <input
              class="filter_input"
              type="number"
              v-model.number="filters.price_to"
              placeholder="Do"
              @change="applyFilters"
              min="0"
            />
          </div>
          <div class="filter_hint">Auta bez podanej ceny zostaną pominięte</div>
        </div>

        <div class="filter_group">
          <label class="filter_label">Przebieg (km)</label>
          <div class="range_row">
            <input
              class="filter_input"
              type="number"
              v-model.number="filters.mileage_from"
              placeholder="Od"
              @change="applyFilters"
              min="0"
            />
            <span class="range_sep">–</span>
            <input
              class="filter_input"
              type="number"
              v-model.number="filters.mileage_to"
              placeholder="Do"
              @change="applyFilters"
              min="0"
            />
          </div>
        </div>

        <div class="filter_group">
          <label class="filter_label">Skrzynia biegów</label>
          <select class="filter_input" v-model="filters.gear_type" @change="applyFilters">
            <option value="">Wszystkie</option>
            <option v-for="code in gearOptions" :key="code" :value="code">
              {{ gearLabel(code) }} ({{ code }})
            </option>
          </select>
        </div>

        <div class="filter_group">
          <label class="filter_label">Ocena stanu</label>
          <select class="filter_input" v-model="filters.auction_rating" @change="applyFilters">
            <option value="">Wszystkie</option>
            <option v-for="r in ratingOptions" :key="r" :value="r">
              {{ r }} — {{ ratingLabel(r) }}
            </option>
          </select>
        </div>

        <div class="filter_group">
          <label class="filter_label">Kierownica</label>
          <div class="pill_row">
            <button
              class="pill"
              :class="{ active: filters.steering_wheel === '' }"
              @click="setSteering('')"
            >Wszystkie</button>
            <button
              class="pill"
              :class="{ active: filters.steering_wheel === 'lhd' }"
              @click="setSteering('lhd')"
            >Lewa (LHD)</button>
            <button
              class="pill"
              :class="{ active: filters.steering_wheel === 'rhd' }"
              @click="setSteering('rhd')"
            >Prawa (RHD)</button>
          </div>
        </div>

        <a class="clear_link" @click="clearFilters" v-if="activeFilterCount > 0">
          Wyczyść filtry
        </a>
      </aside>

      <!-- Results -->
      <div class="content_col">
        <div class="results_bar">
          <div class="view_toggle">
            <button
              class="view_btn"
              :class="{ active: viewMode === 'list' }"
              @click="setView('list')"
              title="Jedna kolumna"
              aria-label="Jedna kolumna"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="1" y="2" width="14" height="3.2" rx="1" fill="currentColor" />
                <rect x="1" y="6.4" width="14" height="3.2" rx="1" fill="currentColor" />
                <rect x="1" y="10.8" width="14" height="3.2" rx="1" fill="currentColor" />
              </svg>
            </button>
            <button
              class="view_btn"
              :class="{ active: viewMode === 'grid' }"
              @click="setView('grid')"
              title="Trzy kolumny"
              aria-label="Trzy kolumny"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="1" y="1" width="4" height="4" rx="1" fill="currentColor" />
                <rect x="6" y="1" width="4" height="4" rx="1" fill="currentColor" />
                <rect x="11" y="1" width="4" height="4" rx="1" fill="currentColor" />
                <rect x="1" y="6" width="4" height="4" rx="1" fill="currentColor" />
                <rect x="6" y="6" width="4" height="4" rx="1" fill="currentColor" />
                <rect x="11" y="6" width="4" height="4" rx="1" fill="currentColor" />
                <rect x="1" y="11" width="4" height="4" rx="1" fill="currentColor" />
                <rect x="6" y="11" width="4" height="4" rx="1" fill="currentColor" />
                <rect x="11" y="11" width="4" height="4" rx="1" fill="currentColor" />
              </svg>
            </button>
          </div>
        </div>
        <div v-if="loaded">
          <div class="content" :class="viewMode === 'grid' ? 'grid_view' : 'list_view'">
            <div class="results_empty" v-if="empty">
              Niestety nie znaleźliśmy żadnych samochodów spełniających dane kryteria
            </div>
            <JdmCarListItem
              v-for="offer in offers"
              :key="offer.post_id"
              :car_data="offer"
              :compact="viewMode === 'grid'"
            />
            <div v-if="loadingMore" class="loading_more">
              <div class="spinner_small"></div>
              <span>Ładowanie więcej...</span>
            </div>
          </div>
        </div>
        <div v-else class="loading_wrap">
          <div class="spinner"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import JdmCarListItem from "@/components/JdmCarListItem.vue";
import axios from "axios";
import { GEAR_OPTIONS, RATING_OPTIONS, gearLabel, ratingLabel } from "@/utils/jdm";

const EMPTY_FILTERS = {
  maker: "",
  model: "",
  year_from: null,
  year_to: null,
  price_from: null,
  price_to: null,
  mileage_from: null,
  mileage_to: null,
  gear_type: "",
  auction_rating: "",
  steering_wheel: "",
};

export default {
  components: { JdmCarListItem },
  data() {
    return {
      offers: [],
      page: 1,
      max_page: 0,
      empty: false,
      loaded: false,
      loadingMore: false,
      makes: [],
      models: [],
      makerSearch: "",
      modelSearch: "",
      gearOptions: GEAR_OPTIONS,
      ratingOptions: RATING_OPTIONS,
      viewMode: localStorage.getItem("jdm_view_mode") === "grid" ? "grid" : "list",
      filters: { ...EMPTY_FILTERS },
    };
  },
  computed: {
    activeFilterCount() {
      return Object.keys(EMPTY_FILTERS).filter((key) => {
        const value = this.filters[key];
        return value !== "" && value !== null && value !== undefined;
      }).length;
    },
    filteredMakes() {
      const q = this.makerSearch.trim().toLowerCase();
      if (!q) return this.makes;
      return this.makes.filter((m) => m.toLowerCase().includes(q));
    },
    filteredModels() {
      const q = this.modelSearch.trim().toLowerCase();
      if (!q) return this.models;
      return this.models.filter((m) => m.toLowerCase().includes(q));
    },
  },
  beforeMount() {
    // Deep-link ze strony nadrzędnej: /search?lot_number=<id aukcji> -> widok aukcji.
    // Pozostałe parametry (filtry) przekazujemy dalej, aby przycisk "Wróć do
    // wyników" mógł je odtworzyć na stronie nadrzędnej.
    if (this.$route.query.lot_number) {
      const { lot_number, ...rest } = this.$route.query;
      this.$router.replace({
        name: "auction",
        params: { id: lot_number },
        query: rest,
      });
      return;
    }
    const q = this.$route.query;
    if (q.car_brand) this.filters.maker = q.car_brand;
    if (q.car_model) this.filters.model = q.car_model;
    if (q.year_from) this.filters.year_from = Number(q.year_from);
    if (q.year_to) this.filters.year_to = Number(q.year_to);
    if (q.price_from) this.filters.price_from = Number(q.price_from);
    if (q.price_to) this.filters.price_to = Number(q.price_to);
    if (q.mileage_from) this.filters.mileage_from = Number(q.mileage_from);
    if (q.mileage_to) this.filters.mileage_to = Number(q.mileage_to);
    // `transmission` w URL zostaje dla linków ze strony nadrzędnej (WordPress),
    // ale niesie kod JDM (AT, FA, MT...) trafiający do filtra `gear_type`.
    if (q.transmission) this.filters.gear_type = q.transmission;
    if (q.rating) this.filters.auction_rating = q.rating;
    if (q.steering) this.filters.steering_wheel = q.steering;
    this.loadMakes();
    if (this.filters.maker) this.loadModels(this.filters.maker);
    this.getOffers();
    this.setupScrollListener();
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    async loadMakes() {
      try {
        const { data } = await axios.get(
          process.env.VUE_APP_API_HOST + "/japan/get-makes",
          { headers: { "X-API-Key": process.env.VUE_APP_API_KEY } }
        );
        const makesData = data.result || data;
        this.makes = makesData.map((m) => m.make || m.name).filter(Boolean);
      } catch { /* non-critical */ }
    },
    async loadModels(makerName) {
      try {
        const { data } = await axios.get(
          process.env.VUE_APP_API_HOST + "/japan/get-models-by-make",
          {
            params: { maker: makerName },
            headers: { "X-API-Key": process.env.VUE_APP_API_KEY },
          }
        );
        const results = data.result || [];
        this.models = results.map((m) => m.name || m.model).filter(Boolean);
      } catch { /* non-critical */ }
    },
    async selectMaker(val) {
      this.filters.maker = val;
      this.filters.model = "";
      this.models = [];
      this.modelSearch = "";
      if (val) await this.loadModels(val);
      this.applyFilters();
    },
    selectModel(val) {
      this.filters.model = val;
      this.applyFilters();
    },
    setView(mode) {
      this.viewMode = mode;
      localStorage.setItem("jdm_view_mode", mode);
    },
    // Wystawione dla szablonu — Options API nie widzi importów bezpośrednio.
    gearLabel,
    ratingLabel,
    setSteering(val) {
      this.filters.steering_wheel = val;
      this.applyFilters();
    },
    applyFilters() {
      this.syncUrl();
      this.offers = [];
      this.page = 1;
      this.max_page = 0;
      this.getOffers();
    },
    syncUrl() {
      // Trzymaj aktywne filtry w adresie URL, dzięki czemu powrót z karty
      // aukcji (przycisk "Wróć") przywraca wcześniej wybrane filtry/modele.
      const f = this.filters;
      const q = {};
      if (f.maker) q.car_brand = f.maker;
      if (f.model) q.car_model = f.model;
      if (f.year_from) q.year_from = f.year_from;
      if (f.year_to) q.year_to = f.year_to;
      if (f.price_from) q.price_from = f.price_from;
      if (f.price_to) q.price_to = f.price_to;
      if (f.mileage_from) q.mileage_from = f.mileage_from;
      if (f.mileage_to) q.mileage_to = f.mileage_to;
      if (f.gear_type) q.transmission = f.gear_type;
      if (f.auction_rating) q.rating = f.auction_rating;
      if (f.steering_wheel) q.steering = f.steering_wheel;
      this.$router.replace({ query: q }).catch(() => {});
    },
    clearFilters() {
      this.filters = { ...EMPTY_FILTERS };
      this.models = [];
      this.makerSearch = "";
      this.modelSearch = "";
      this.applyFilters();
    },
    buildParams(page) {
      // Nazwy pokrywają się z parametrami /japan/get-cars — patrz
      // backend/JDM_API_SPEC.md §5.
      const p = { per_page: 10, page };
      for (const [key, value] of Object.entries(this.filters)) {
        if (value !== "" && value !== null && value !== undefined) p[key] = value;
      }
      return p;
    },
    setupScrollListener() {
      window.addEventListener("scroll", this.handleScroll);
    },
    handleScroll() {
      if (this.loadingMore || this.page >= this.max_page) return;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      if (scrollTop + windowHeight >= documentHeight - 200) this.getMoreOffers();
    },
    async getOffers() {
      this.loaded = false;
      try {
        const { data } = await axios.get(
          process.env.VUE_APP_API_HOST + "/japan/get-cars",
          { params: this.buildParams(1), headers: { "X-API-Key": process.env.VUE_APP_API_KEY } }
        );
        this.empty = data.pagination.total === 0;
        this.max_page = data.pagination.total_pages;
        this.offers = data.result;
        this.loaded = true;
      } catch {
        this.empty = true;
        this.loaded = true;
      }
    },
    async getMoreOffers() {
      if (this.loadingMore || this.page >= this.max_page) return;
      this.loadingMore = true;
      this.page++;
      try {
        const { data } = await axios.get(
          process.env.VUE_APP_API_HOST + "/japan/get-cars",
          { params: this.buildParams(this.page), headers: { "X-API-Key": process.env.VUE_APP_API_KEY } }
        );
        this.offers = this.offers.concat(data.result);
      } catch { /* ignore */ }
      finally { this.loadingMore = false; }
    },
  },
};
</script>

<style scoped>
.search_view {
  background: #0d0d0d;
  min-height: 100vh;
  padding: 32px 28px;
  font-family: 'DM Sans', sans-serif;
  box-sizing: border-box;
}

.layout {
  max-width: 1680px;
  margin: 0 auto;
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

/* ── Sidebar ── */
.sidebar {
  width: 280px;
  flex-shrink: 0;
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.07);
  position: sticky;
  top: 32px;
}

.sidebar_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.sidebar_title {
  font-size: 11px;
  font-weight: 700;
  color: #d21f1f;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.active_badge {
  font-size: 11px;
  font-weight: 700;
  background: #d21f1f;
  color: #fff;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.filter_group {
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.filter_label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 9px;
}

.filter_select,
.filter_input {
  width: 100%;
  padding: 9px 11px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  box-sizing: border-box;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  transition: border-color 0.12s;
}

.filter_select:focus,
.filter_input:focus {
  border-color: rgba(255, 255, 255, 0.28);
}

.filter_select:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.filter_select option {
  background: #1c1c1c;
  color: #fff;
}

.filter_input::placeholder {
  color: rgba(255, 255, 255, 0.22);
}

select.filter_input {
  cursor: pointer;
}

select.filter_input option {
  background: #1c1c1c;
  color: #fff;
}

.filter_hint {
  margin-top: 7px;
  font-size: 10px;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.28);
}

.range_row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.range_row .filter_input {
  flex: 1;
  width: 0;
  min-width: 0;
  text-align: center;
}

.range_sep {
  color: rgba(255, 255, 255, 0.2);
  font-size: 14px;
  flex-shrink: 0;
}

.pill_row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pill {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.12s, color 0.12s, background 0.12s;
}

.pill:hover:not(.active) {
  border-color: rgba(255, 255, 255, 0.22);
  color: rgba(255, 255, 255, 0.8);
}

.pill.active {
  background: #d21f1f;
  border-color: #d21f1f;
  color: #fff;
  font-weight: 600;
}

.clear_link {
  display: block;
  padding: 14px 20px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
  text-align: center;
  cursor: pointer;
  transition: color 0.12s;
  user-select: none;
}

.clear_link:hover {
  color: #d21f1f;
}

/* ── Content ── */
.content_col {
  flex: 1;
  min-width: 0;
}

.content.list_view {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.content.grid_view {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  align-items: start;
}

.content.grid_view .results_empty,
.content.grid_view .loading_more {
  grid-column: 1 / -1;
}

@media (max-width: 1100px) {
  .content.grid_view {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .content.grid_view {
    grid-template-columns: 1fr;
  }
}

/* ── Results bar / view toggle ── */
.results_bar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 14px;
}

.view_toggle {
  display: flex;
  gap: 4px;
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.07);
  padding: 4px;
}

.view_btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 30px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: color 0.12s, background 0.12s;
}

.view_btn:hover:not(.active) {
  color: rgba(255, 255, 255, 0.75);
}

.view_btn.active {
  background: #d21f1f;
  color: #fff;
}

/* ── Listed filter options ── */
.filter_search {
  margin-bottom: 9px;
}

.option_list {
  display: flex;
  flex-direction: column;
  max-height: 230px;
  overflow-y: auto;
  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.18) transparent;
}

/* WebKit / Chromium – cienki, ciemny scrollbar dopasowany do sidebara */
.option_list::-webkit-scrollbar {
  width: 6px;
}

.option_list::-webkit-scrollbar-track {
  background: transparent;
}

.option_list::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.18);
  border-radius: 10px;
}

.option_list::-webkit-scrollbar-thumb:hover {
  background-color: #d21f1f;
}

.option_item {
  text-align: left;
  padding: 8px 11px;
  background: transparent;
  border: none;
  border-left: 2px solid transparent;
  color: rgba(255, 255, 255, 0.55);
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  cursor: pointer;
  transition: color 0.12s, background 0.12s, border-color 0.12s;
}

.option_item:hover:not(.active) {
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.9);
}

.option_item.active {
  color: #fff;
  font-weight: 600;
  border-left-color: #d21f1f;
  background: rgba(210, 31, 31, 0.12);
}

.option_empty,
.option_hint {
  padding: 8px 11px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.28);
}

.results_empty {
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
  padding: 60px 0;
  text-align: center;
}

.loading_wrap {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 3px solid rgba(255, 255, 255, 0.08);
  border-top-color: #d21f1f;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading_more {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: 24px;
  color: rgba(255, 255, 255, 0.3);
  font-size: 13px;
}

.spinner_small {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.08);
  border-top-color: #d21f1f;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
  flex-shrink: 0;
}

@media (max-width: 820px) {
  .search_view {
    padding: 16px;
  }

  .layout {
    flex-direction: column;
    gap: 16px;
  }

  .sidebar {
    width: 100%;
    position: static;
  }

  .pill_row {
    flex-direction: row;
  }

  .pill {
    flex: 1;
    text-align: center;
  }
}
</style>
